<template>
    <div class="tw-relative">
        <div 
            @mouseenter="showTooltip = true"
            @mouseleave="showTooltip = false"
            @pointerdown="handlePointer"
            class="tw-w-4 tw-h-4 tw-rounded-full tw-border tw-border-gray-400 tw-flex tw-items-center tw-justify-center tw-cursor-help tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-xs tw-text-gray-600"
            role="button"
            tabindex="0"
            :aria-label="ariaLabel"
            @keydown.enter="toggleTooltip"
            @keydown.space.prevent="toggleTooltip"
        >
            ?
        </div>
        <div 
            v-if="showTooltip"
            :class="tooltipClasses"
            role="tooltip"
        >
            <div class="tw-relative">
                <slot>{{ message }}</slot>
                <!-- Tooltip arrow -->
                <div :class="arrowClasses"></div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InfoTooltip',
    props: {
        message: {
            type: String,
            required: true,
        },
        ariaLabel: {
            type: String,
            default: 'More information',
        },
        position: {
            type: String,
            default: 'top',
            validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
        },
        width: {
            type: String,
            default: 'tw-w-64',
        },
    },
    data() {
        return {
            showTooltip: false,
        };
    },
    computed: {
        tooltipClasses() {
            const baseClasses = 'tw-absolute tw-p-3 tw-bg-gray-800 tw-text-white tw-text-sm tw-rounded-lg tw-shadow-lg tw-z-50';
            const widthClass = this.width;
            
            const positionClasses = {
                top: 'tw-bottom-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mb-2',
                bottom: 'tw-top-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mt-2',
                left: 'tw-right-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-mr-2',
                right: 'tw-left-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-ml-2',
            };
            
            return `${baseClasses} ${widthClass} ${positionClasses[this.position]}`;
        },
        arrowClasses() {
            const arrowPositions = {
                top: 'tw-absolute tw-top-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-w-0 tw-h-0 tw-border-l-4 tw-border-r-4 tw-border-t-4 tw-border-l-transparent tw-border-r-transparent tw-border-t-gray-800',
                bottom: 'tw-absolute tw-bottom-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-w-0 tw-h-0 tw-border-l-4 tw-border-r-4 tw-border-b-4 tw-border-l-transparent tw-border-r-transparent tw-border-b-gray-800',
                left: 'tw-absolute tw-left-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-0 tw-h-0 tw-border-t-4 tw-border-b-4 tw-border-l-4 tw-border-t-transparent tw-border-b-transparent tw-border-l-gray-800',
                right: 'tw-absolute tw-right-full tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-0 tw-h-0 tw-border-t-4 tw-border-b-4 tw-border-r-4 tw-border-t-transparent tw-border-b-transparent tw-border-r-gray-800',
            };
            
            return arrowPositions[this.position];
        },
    },
    mounted() {
        document.addEventListener('pointerdown', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('pointerdown', this.handleClickOutside);
    },
    methods: {
        toggleTooltip() {
            this.showTooltip = !this.showTooltip;
        },
        handlePointer(event) {
            // Prevent zoom on double-tap for touch devices
            if (event.pointerType === 'touch') {
                event.preventDefault();
            }
            this.toggleTooltip();
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.showTooltip = false;
            }
        },
    },
};
</script>

<style scoped>
/* Additional tooltip-specific styles if needed */
</style>
